import type { ApiResponse } from 'src/api/services/api.types';
import type { UnitStatReportViewModel } from 'src/api/types/UnitStatReportViewModel';
import type { StateLookupOption, UpdatePolicyRequest } from 'src/api/services/policy.service';

import { useBoolean } from 'minimal-shared/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { useParams, useBlocker, useLocation, useNavigate } from 'react-router';
import { useRef, useState, useEffect, useCallback, useTransition, startTransition } from 'react';

import Box from '@mui/material/Box';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import TabContext from '@mui/lab/TabContext';
import { Tab, Skeleton, Typography } from '@mui/material';

import { paths } from 'src/routes/paths';

import { PageAlertMessage } from 'src/diamond-components';
import { useUpdatePolicyMutation } from 'src/api/hooks/usePolicyMutations';
import { SEARCH_RESTORE_QUERY_PARAM, SEARCH_RESTORE_QUERY_PARAM_VALUE } from 'src/pages/dashboard/advanced-search/constants';
import {
  usePolicyById,
  useClassCodeLookups,
  useEndorsementTypeLookups,
  usePolicyInformationLookups,
  useEndorsementDescriptorTypeLookups,
} from 'src/api/hooks/usePolicyQueries';
import {
  getSearchOriginFromLocationState,
  getSearchRestoreSnapshotFromLocationState,
  removeSearchRestoreNavigationStateFromLocationState,
} from 'src/pages/dashboard/advanced-search/search-restore-state';
import {
  useStateLookups,
  usePremiumsByPolicyId,
  useUnitStatUsrLookups,
  useEndorsementsByPolicyId,
  usePolicyPeriodsByPolicyId,
  usePolicyNamesAndAddresses,
  usePolicyExposuresByPolicyId,
  useUnitStatReportVersionsByPolicyId,
  useUnitStatReportByPolicyIdAndReportNumber,
} from 'src/api/hooks/usePolicyDetailsQueries';

import { ActionButton } from 'src/sections/employer-data/shared/components/action-button';
import { PolicyStatusChips } from 'src/sections/employer-data/shared/components/status-chips';
import DynamicBreadcrumbs from 'src/sections/employer-data/dynamic-breadcrumbs/dynamic-breadcrumbs';

import PolicyPeriods from './policy-periods/policy-periods';
import StatusHistory from './status-history/status-history';
import { getPrimaryPolicyName } from '../shared/utils/policyName';
import { SaveConfirmModal } from './components/save-confirm-modal';
import { CancelConfirmModal } from './components/cancel-confirm-modal';
import PolicyInformation from './policy-information/policy-information';
import ComplianceHistory from './compliance-history/compliance-history';
import { MovePolicyModal } from '../policy/components/move-policy-modal';
import NamesAndAddresses from './names-and-addresses/names-and-addresses';
import { MovePolicyButton } from '../policy/components/move-policy-button';
import TransactionHistory from './transaction-history/transaction-history';
import { showError, formatDollarAmount } from '../shared/utils/policyUtils';
import Premium, { type PremiumHandle, type PremiumDraftDelta } from './premiums/premiums';
import UnitStatReports, { type UnitStatReportsHandle } from './unit-stat-reports/unit-stat-reports';
import {
  buildNamesAndAddressesPayload,
  validateNamesAndAddressesDraft,
  applyNamesAndAddressesDraftDelta,
  initializeNamesAndAddressesDraft,
  getNamesAndAddressesChangedFieldSummaries,
} from './names-and-addresses/names-and-addresses.helpers';
import {
  buildPolicyPeriodPayload,
  validatePolicyPeriodDraft,
  initializePolicyPeriodDraft,
  type PolicyPeriodDraftDelta,
  applyPolicyPeriodsDraftDelta,
  buildPolicyPeriodChangedFieldSummaries,
} from './policy-periods/policy-periods-edit.types';
import Endorsements, {
  validateEndorsementRows,
  type EndorsementsHandle,
  applyEndorsementsDraftDelta,
  type EndorsementsDraftDelta,
  toDraft as toEndorsementDraft,
  toPayload as toEndorsementsPayload,
  getEndorsementsChangedFieldSummaries,
} from './endorsements/endorsements';
import ClassCodes, {
  toDraftRow,
  validateClassCodeRows,
  type ClassCodesHandle,
  applyClassCodesDraftDelta,
  type ClassCodesDraftDelta,
  getClassCodesChangedFieldSummaries,
  getClassCodesExposuresPayloadByPeriod,
  getClassCodesDeletedPolicyExposureIdsByPeriod,
} from './class-codes/class-codes';
import {
  hasFieldErrors,
  findCodeByDescription,
  buildPolicySectionPayload,
  buildStatePremiumsPayload,
  findStateCodeByAbbreviation,
  buildChangedFieldSummaryLines,
  applyPolicyInformationDraftDelta,
  validateDraft as validatePolicyInformationDraft,
  initializeDraft as initializePolicyInformationDraft,
} from './policy-information/policy-information.helpers';
import {
  type UnitStatHeaderDraft,
  createUnitStatHeaderDraft,
  type UnitStatHeaderLookups,
  getUnitStatHeaderDraftDelta,
  validateUnitStatHeaderDraft,
  applyUnitStatHeaderDraftDelta,
  type UnitStatReportsDraftDelta,
  createUnitStatHeaderUpdateRequest,
  getUnitStatHeaderChangedFieldSummaries,
} from './unit-stat-reports/tabs/header/unit-stat-header-edit.helpers';
import {
  applyUnitStatExposureDraftDelta,
  createUnitStatExposureDraft,
  createUnitStatExposureRequests,
  getUnitStatExposureChangedFieldSummaries,
  validateUnitStatExposureDraft,
  type UnitStatExposureDraftDelta,
} from './unit-stat-reports/tabs/exposures/unit-stat-exposures-edit.helpers';

import type { PolicyPeriodsHandle } from './policy-periods/policy-periods.types';
import type { PolicyInformationHandle } from './policy-information/policy-information';
import type { PolicyInformationDraftDelta } from './policy-information/policy-information.types';
import type { NamesAndAddressesHandle, NamesAndAddressesDraftDelta } from './names-and-addresses/names-and-addresses.types';

// ----------------------------------------------------------------------

const ED_POLICY_TABS = [
  { value: 'policyInformation', label: 'Policy information' },
  { value: 'policyPeriods', label: 'Policy periods' },
  { value: 'namesAndAddresses', label: 'Names & addresses' },
  { value: 'premium', label: 'Premium' },
  { value: 'classCodes', label: 'Class codes' },
  { value: 'unitStatReports', label: 'Unit stat reports' },
  { value: 'endorsements', label: 'Endorsements' },
  { value: 'statusHistory', label: 'Status history' },
  { value: 'complianceHistory', label: 'Compliance history' },
  { value: 'transactionHistory', label: 'Transaction history' },
];

// Stable reference so memoized children don't re-render while lookups are loading.
const EMPTY_STATE_LOOKUPS: StateLookupOption[] = [];

// Shown in place of tab content while a tab-switch transition is pending during edit mode,
// since the heavier edit-mode tabs no longer stay mounted and can take a moment to render.
function TabContentSkeleton() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 3 }} data-testid="policy-details-tab-skeleton">
      {[...new Array(5)].map((_, index) => (
        <Skeleton key={index} variant="rectangular" height={54} sx={{ borderRadius: 1 }} />
      ))}
    </Box>
  );
}

export function PolicyDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  const restoreSnapshot = getSearchRestoreSnapshotFromLocationState(location.state);
  const searchOrigin = getSearchOriginFromLocationState(location.state);
  const canNavigateBackToSearch = searchOrigin === 'policyDetails' && restoreSnapshot !== null;
  const initialTab = (location.state as any)?.tab || 'policyInformation';
  const [tab, setTab] = useState(initialTab);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [saveNote, setSaveNote] = useState('');
  const [saveNoteError, setSaveNoteError] = useState('');
  const [saveErrorMessage, setSaveErrorMessage] = useState('');
  const [changeSummaryLines, setChangeSummaryLines] = useState<string[]>([]);
  const [successMessage, setSuccessMessage] = useState('');
    const [editSession, setEditSession] = useState<{
      snapshot: UpdatePolicyRequest | null;
      draft: UpdatePolicyRequest | null;
    }>({ snapshot: null, draft: null });
  const [policyInfoTabHasErrors, setPolicyInfoTabHasErrors] = useState(false);
  const [namesAndAddressesTabHasErrors, setNamesAndAddressesTabHasErrors] = useState(false);
  const [classCodesTabHasErrors, setClassCodesTabHasErrors] = useState(false);
  const [endorsementsTabHasErrors, setEndorsementsTabHasErrors] = useState(false);
  const [premiumTabHasErrors, setPremiumTabHasErrors] = useState(false);
  const [unitStatReportsTabHasErrors, setUnitStatReportsTabHasErrors] = useState(false);
  const policyInformationRef = useRef<PolicyInformationHandle>(null);
  const policyPeriodsRef = useRef<PolicyPeriodsHandle>(null);
  const namesAndAddressesRef = useRef<NamesAndAddressesHandle>(null);
  const classCodesRef = useRef<ClassCodesHandle>(null);
  const endorsementsRef = useRef<EndorsementsHandle>(null);
  const premiumRef = useRef<PremiumHandle>(null);
  const unitStatReportsRef = useRef<UnitStatReportsHandle>(null);
  // Changed-values-only delta cache per tab, so switching tabs doesn't lose in-progress edits
  // without duplicating unedited read data (see openspec/changes/refactor-policy-edits-for-performance).
  const policyInformationDeltaRef = useRef<PolicyInformationDraftDelta | null>(null);
  const policyPeriodsDeltaRef = useRef<PolicyPeriodDraftDelta | null>(null);
  const namesAndAddressesDeltaRef = useRef<NamesAndAddressesDraftDelta | null>(null);
  const classCodesDeltaRef = useRef<ClassCodesDraftDelta | null>(null);
  const endorsementsDeltaRef = useRef<EndorsementsDraftDelta | null>(null);
  const premiumDeltaRef = useRef<PremiumDraftDelta | null>(null);
  const unitStatReportsDeltaRef = useRef<UnitStatReportsDraftDelta | null>(null);
  const queryClient = useQueryClient();
  const updatePolicy = useUpdatePolicyMutation();
  const movePolicyModal = useBoolean();
  // Tracks the tab-switch transition specifically (separate from the bare startTransition calls
  // below) so we can show a skeleton instead of stale tab content while it's pending.
  const [isTabPending, startTabTransition] = useTransition();
  const isTabMounted = (tabValue: string) => tab === tabValue;

  const getPolicyInformationHasPendingChanges = () =>
    isTabMounted('policyInformation')
      ? (policyInformationRef.current?.hasPendingChanges() ?? false)
      : Object.keys(policyInformationDeltaRef.current ?? {}).length > 0;
  const getPolicyPeriodsHasPendingChanges = () =>
    isTabMounted('policyPeriods')
      ? (policyPeriodsRef.current?.hasPendingChanges() ?? false)
      : Object.keys(policyPeriodsDeltaRef.current ?? {}).length > 0;
  const getNamesAndAddressesHasPendingChanges = () =>
    isTabMounted('namesAndAddresses')
      ? (namesAndAddressesRef.current?.hasPendingChanges() ?? false)
      : Object.keys(namesAndAddressesDeltaRef.current ?? {}).length > 0;
  const getClassCodesHasPendingChanges = () =>
    isTabMounted('classCodes')
      ? (classCodesRef.current?.hasPendingChanges() ?? false)
      : Boolean(classCodesDeltaRef.current?.rowPatches || classCodesDeltaRef.current?.newRows || classCodesDeltaRef.current?.deletedIds);
  const getEndorsementsHasPendingChanges = () =>
    isTabMounted('endorsements')
      ? (endorsementsRef.current?.hasPendingChanges() ?? false)
      : Object.keys(endorsementsDeltaRef.current ?? {}).length > 0;
  const getPremiumHasPendingChanges = () =>
    isTabMounted('premium')
      ? (premiumRef.current?.hasPendingChanges() ?? false)
      : Object.keys(premiumDeltaRef.current ?? {}).length > 0;
  const getUnitStatReportsHasPendingChanges = () =>
    isTabMounted('unitStatReports')
      ? (unitStatReportsRef.current?.hasPendingChanges() ?? false)
      : Object.keys(unitStatReportsDeltaRef.current ?? {}).length > 0;
  const getAnyTabHasPendingChanges = () =>
    getPolicyInformationHasPendingChanges() ||
    getPolicyPeriodsHasPendingChanges() ||
    getNamesAndAddressesHasPendingChanges() ||
    getClassCodesHasPendingChanges() ||
    getEndorsementsHasPendingChanges() ||
    getPremiumHasPendingChanges() ||
    getUnitStatReportsHasPendingChanges();
  const leavePageBlocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isEditMode &&
      currentLocation.pathname !== nextLocation.pathname &&
      getAnyTabHasPendingChanges()
  );

  useEffect(() => {
    if ((location.state as any)?.tab) {
      const targetTab = (location.state as any).tab;
      setTab(targetTab);
    }
  }, [location.state]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Only warn on refresh/close when there are real unsaved edits, matching the Cancel button's check.
      const hasPendingChanges = getAnyTabHasPendingChanges();

      if (!hasPendingChanges) {
        return;
      }

      event.preventDefault();
    };

    if (isEditMode) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      if (isEditMode) {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleBeforeUnload reads live ref/delta state at event-fire time, not effect-setup time; only isEditMode should retrigger (re)subscription.
  }, [isEditMode]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    // Deferred so switching into a data-heavy tab (e.g. many Names & Addresses entries) doesn't
    // block the click; same technique already used for the isEditMode toggle above.
    startTabTransition(() => {
      setTab(newValue);
      const nextState = {
        ...removeSearchRestoreNavigationStateFromLocationState(location.state),
        tab: newValue,
      };

      navigate(location.pathname, { state: nextState, replace: true });
    });
  };

  const handleBackToSearchClick = () => {
    if (!restoreSnapshot) {
      return;
    }

    navigate(
      `${paths.dashboard.advanced_search}?tab=${restoreSnapshot.tabId}&${SEARCH_RESTORE_QUERY_PARAM}=${SEARCH_RESTORE_QUERY_PARAM_VALUE}`,
      {
        state: {
          searchTab: restoreSnapshot.tabId,
          searchRestoreSnapshot: restoreSnapshot,
        },
      }
    );
  };

  const {
    comboId,
    coverageId,
    policyId: policyIdStr,
  } = useParams<{ comboId: string; coverageId: string; policyId: string }>();
  const policyId = Number(policyIdStr);

  const { data, isSuccess, isError, error, isLoading, isPending, refetch } = usePolicyById(
    Number(comboId),
    Number(coverageId),
    policyId
  );
  const { data: editLookups } = usePolicyInformationLookups();
  const { data: premiumDetails } = usePremiumsByPolicyId(Number(comboId), Number(coverageId), policyId);
  // Lifted so pending edits in a tab that is currently unmounted (not the active tab) can still be
  // reconstructed, validated, and saved from the cached delta + this view data (see Decision 3/5 in design.md).
  const { data: policyPeriodsData } = usePolicyPeriodsByPolicyId(Number(comboId), Number(coverageId), policyId);
  const { data: namesAndAddressesData } = usePolicyNamesAndAddresses(Number(comboId), Number(coverageId), policyId);
  const { data: exposuresData } = usePolicyExposuresByPolicyId(Number(comboId), Number(coverageId), policyId);
  const { data: endorsementsData } = useEndorsementsByPolicyId(Number(comboId), Number(coverageId), policyId);
  const { data: classCodeLookupsData } = useClassCodeLookups();
  const { data: endorsementTypeLookupsData } = useEndorsementTypeLookups();
  const { data: endorsementDescriptorTypeLookupsData } = useEndorsementDescriptorTypeLookups();
  const { data: unitStatReportVersions } = useUnitStatReportVersionsByPolicyId(Number(comboId), Number(coverageId), policyId);
  const primaryUnitStatVersion = unitStatReportVersions?.data?.[0];
  const { data: primaryUnitStatReport } = useUnitStatReportByPolicyIdAndReportNumber(
    Number(comboId),
    Number(coverageId),
    policyId,
    primaryUnitStatVersion?.reportNumber,
    primaryUnitStatVersion?.correctionSequenceNumber
  );
  const { data: unitStatUsrLookupsData } = useUnitStatUsrLookups();
  const { data: unitStatStateLookupsData } = useStateLookups();
  const handleRefreshPolicy = useCallback(async () => {
    await refetch();
  }, [refetch]);
  if (isLoading || isPending) {
    return <Skeleton variant="text" sx={{ fontSize: '1rem' }} data-testid="skeleton" />;
  }
  if (isError || !isSuccess || !data?.data) {
    return showError(policyId, error?.message ?? '');
  }

  // Loading state is just "render blank stuff"
  // so ignore loading state here

  const viewPolicy = data?.data;
  const primaryPolicyName = getPrimaryPolicyName(viewPolicy?.policyNames ?? undefined);

  // --- Unmounted-tab reconstruction -----------------------------------------------------------
  // When a tab is not the currently active one, its component (and imperative-handle ref) is
  // unmounted. To still validate/summarize/save its pending edits, reconstruct its draft by
  // merging the cached changed-values-only delta onto a fresh snapshot built from the same view
  // data + lookups the component would use, via the pure helper functions extracted from each tab.
  const reconstructPolicyInformation = () => {
    let snapshot = initializePolicyInformationDraft(viewPolicy);
    if (editLookups) {
      snapshot = {
        ...snapshot,
        minimumPremiumStateCode:
          snapshot.minimumPremiumStateCode > 0
            ? snapshot.minimumPremiumStateCode
            : findStateCodeByAbbreviation(editLookups.stateLookups, viewPolicy.minimumPremiumState),
        legalNatureCode:
          snapshot.legalNatureCode ||
          findCodeByDescription(editLookups.legalNatureLookups, viewPolicy.legalNatureEnumDescription),
        retroRatingCode:
          snapshot.retroRatingCode ||
          findCodeByDescription(editLookups.retroRatingLookups, viewPolicy.retroRatingTypeEnumDescription),
        policyTypeCode:
          snapshot.policyTypeCode ||
          findCodeByDescription(editLookups.policyTypeLookups, viewPolicy.policyTypeEnumDescription),
        planTypeCode:
          snapshot.planTypeCode ||
          findCodeByDescription(editLookups.policyPlanTypeLookups, viewPolicy.planTypeEnumDescription) ||
          findCodeByDescription(
            editLookups.marketLookups.map((item) => ({ code: item.code, description: item.description })),
            viewPolicy.planTypeEnumDescription
          ),
      };
    }
    const draft = applyPolicyInformationDraftDelta(snapshot, policyInformationDeltaRef.current);
    return { snapshot, draft };
  };

  const reconstructPolicyPeriods = () => {
    const periods = policyPeriodsData?.data ?? [];
    const snapshots = new Map(periods.map((period) => [period.policyPeriodId ?? 0, initializePolicyPeriodDraft(period)]));
    const drafts = new Map(
      [...snapshots.entries()].map(([periodId, snapshot]) => [
        periodId,
        applyPolicyPeriodsDraftDelta({ [periodId]: snapshot }, policyPeriodsDeltaRef.current)[periodId],
      ])
    );
    return { snapshots, drafts };
  };

  const reconstructNamesAndAddresses = () => {
    const snapshot = initializeNamesAndAddressesDraft(namesAndAddressesData?.data ?? ({} as never));
    const draft = applyNamesAndAddressesDraftDelta(snapshot, namesAndAddressesDeltaRef.current);
    return { snapshot, draft };
  };

  const reconstructClassCodes = () => {
    const sortedExposures = [...(exposuresData?.data ?? [])].sort((a, b) =>
      (a.class?.classCode ?? '').localeCompare(b.class?.classCode ?? '', undefined, { numeric: true })
    );
    const snapshotRows = sortedExposures.map((exposure, index) => toDraftRow(exposure, index));
    const { rows: draftRows, deletedIds } = applyClassCodesDraftDelta(snapshotRows, classCodesDeltaRef.current);
    return { snapshotRows, draftRows, deletedIds };
  };

  const reconstructEndorsements = () => {
    const snapshotRows = (endorsementsData?.data ?? []).map((item, index) => toEndorsementDraft(item, index));
    const draftRows = applyEndorsementsDraftDelta(snapshotRows, endorsementsDeltaRef.current);
    return { snapshotRows, draftRows };
  };

  const reconstructPremium = () => {
    const freshValue = String(Math.trunc(premiumDetails?.data?.[0]?.totalPolicyPremium ?? 0));
    const draftValue = premiumDeltaRef.current?.totalPolicyPremium ?? freshValue;
    return { snapshotValue: freshValue, draftValue };
  };

  const reconstructUnitStatReports = () => {
    const lookups: UnitStatHeaderLookups = {
      states: unitStatStateLookupsData?.data ?? [],
      correctionTypes: unitStatUsrLookupsData?.data?.correctionTypes ?? [],
      coverageTypes: unitStatUsrLookupsData?.data?.coverageTypes ?? [],
      marketTypes: unitStatUsrLookupsData?.data?.marketTypes ?? [],
      estimatedAuditTypes: unitStatUsrLookupsData?.data?.estimatedAuditTypes ?? [],
      lossesSubjectDeductibleTypes: unitStatUsrLookupsData?.data?.lossesSubjectDeductibleTypes ?? [],
      basisDeductibleCalculationTypes: unitStatUsrLookupsData?.data?.basisDeductibleCalculationTypes ?? [],
      lossTypes: unitStatUsrLookupsData?.data?.lossTypes ?? [],
      injuryTypes: unitStatUsrLookupsData?.data?.injuryTypes ?? [],
      claimStatusTypes: unitStatUsrLookupsData?.data?.claimStatusTypes ?? [],
      unitStatusTypes: unitStatUsrLookupsData?.data?.unitStatusTypes ?? [],
    };
    const report = primaryUnitStatReport?.data;
    const snapshot = createUnitStatHeaderDraft(report);
    // The tab isn't mounted here, but every correction with a cached pending delta was fetched (and
    // cached in the query client) the moment the user visited it while the tab was mounted, so each
    // one's report data can still be looked up here to reconstruct its draft.
    const primaryCorrectionKey = primaryUnitStatVersion
      ? `${primaryUnitStatVersion.reportNumber}.${primaryUnitStatVersion.correctionSequenceNumber}`
      : undefined;
    const primaryDelta = primaryCorrectionKey ? (unitStatReportsDeltaRef.current?.[primaryCorrectionKey] ?? null) : null;
    const { exposures: primaryExposureDelta, ...primaryHeaderDelta } = primaryDelta ?? {};
    const draft = applyUnitStatHeaderDraftDelta(snapshot, primaryHeaderDelta);
    const exposureSnapshot = createUnitStatExposureDraft(report?.unitStatReportExposures ?? []);
    const exposureDraft = applyUnitStatExposureDraftDelta(exposureSnapshot, primaryExposureDelta);

    const getReportForCorrectionKey = (correctionKey: string): UnitStatReportViewModel | undefined => {
      if (correctionKey === primaryCorrectionKey) {
        return report;
      }

      const [reportNumberPart, correctionSequenceNumberPart] = correctionKey.split('.');
      return queryClient.getQueryData<ApiResponse<UnitStatReportViewModel>>([
        'policyUnitStatReport',
        Number(comboId),
        Number(coverageId),
        policyId,
        Number(reportNumberPart),
        Number(correctionSequenceNumberPart),
      ])?.data;
    };

    // Every correction with a cached pending delta, reconstructed against its own report snapshot -
    // this is what allows all edited corrections (not just the primary one) to be saved in one request.
    const otherCorrections = Object.keys(unitStatReportsDeltaRef.current ?? {})
      .filter((key) => key !== primaryCorrectionKey)
      .map((key) => {
        const correctionReport = getReportForCorrectionKey(key);
        if (!correctionReport) {
          return undefined;
        }

        const correctionSnapshot = createUnitStatHeaderDraft(correctionReport);
        const correctionDelta = unitStatReportsDeltaRef.current?.[key] ?? null;
        const { exposures: correctionExposureDelta, ...correctionHeaderDelta } = correctionDelta ?? {};
        const correctionDraft = applyUnitStatHeaderDraftDelta(correctionSnapshot, correctionHeaderDelta);
        const correctionExposureSnapshot = createUnitStatExposureDraft(correctionReport.unitStatReportExposures ?? []);
        const correctionExposureDraft = applyUnitStatExposureDraftDelta(correctionExposureSnapshot, correctionExposureDelta);
        return {
          key,
          report: correctionReport,
          snapshot: correctionSnapshot,
          draft: correctionDraft,
          exposureSnapshot: correctionExposureSnapshot,
          exposureDraft: correctionExposureDraft,
        };
      })
      .filter((entry): entry is {
        key: string;
        report: UnitStatReportViewModel;
        snapshot: UnitStatHeaderDraft;
        draft: UnitStatHeaderDraft;
        exposureSnapshot: UnitStatExposureDraftDelta;
        exposureDraft: UnitStatExposureDraftDelta;
      } => entry !== undefined);

    return { report, snapshot, draft, lookups, exposureSnapshot, exposureDraft, otherCorrections };
  };

  const handleEnterEdit = () => {
    const policy = policyInformationRef.current?.getPolicySectionPayload();
    const policyPeriods = policyPeriodsRef.current?.getPolicyPeriodsPayload();
    const currentDraft = policy && policyPeriods
      ? { policy, policyPeriods }
      : null;

    setEditSession({ snapshot: currentDraft, draft: currentDraft });
    setSaveConfirmOpen(false);
    setCancelConfirmOpen(false);
    setSaveNote('');
    setSaveNoteError('');
    setSaveErrorMessage('');
    setChangeSummaryLines([]);

    // Toggling isEditMode re-renders every kept-mounted visited tab at once; defer so the click stays responsive.
    startTransition(() => {
      setIsEditMode(true);
      setPolicyInfoTabHasErrors(false);
      setNamesAndAddressesTabHasErrors(false);
      setClassCodesTabHasErrors(false);
      setEndorsementsTabHasErrors(false);
      setPremiumTabHasErrors(false);
      setUnitStatReportsTabHasErrors(false);
    });
  };

  const handleCancelEdit = () => {
    const hasPendingChanges = getAnyTabHasPendingChanges();

    if (!hasPendingChanges) {
      policyInformationRef.current?.cancelEdit();
      policyPeriodsRef.current?.cancelEdit();
      namesAndAddressesRef.current?.cancelEdit();
      classCodesRef.current?.cancelEdit();
      endorsementsRef.current?.cancelEdit();
      premiumRef.current?.cancelEdit();
      unitStatReportsRef.current?.cancelEdit();
      policyInformationDeltaRef.current = null;
      policyPeriodsDeltaRef.current = null;
      namesAndAddressesDeltaRef.current = null;
      classCodesDeltaRef.current = null;
      endorsementsDeltaRef.current = null;
      premiumDeltaRef.current = null;
      unitStatReportsDeltaRef.current = null;
      startTransition(() => {
        setIsEditMode(false);
        setPolicyInfoTabHasErrors(false);
        setNamesAndAddressesTabHasErrors(false);
        setClassCodesTabHasErrors(false);
        setEndorsementsTabHasErrors(false);
        setPremiumTabHasErrors(false);
        setUnitStatReportsTabHasErrors(false);
      });
      return;
    }

    setCancelConfirmOpen(true);
  };

  const handleOpenSaveConfirm = () => {
    const policyInformationIsValid = isTabMounted('policyInformation')
      ? (policyInformationRef.current?.validateForSave() ?? false)
      : !getPolicyInformationHasPendingChanges() || !hasFieldErrors(validatePolicyInformationDraft(reconstructPolicyInformation().draft));
    const policyPeriodsAreValid = isTabMounted('policyPeriods')
      ? (policyPeriodsRef.current?.validateForSave() ?? false)
      : !getPolicyPeriodsHasPendingChanges() || [...reconstructPolicyPeriods().drafts.values()].every(
          (draft) => Object.keys(validatePolicyPeriodDraft(draft)).length === 0
        );
    const namesAndAddressesAreValid = isTabMounted('namesAndAddresses')
      ? (namesAndAddressesRef.current?.validateForSave() ?? false)
      : !getNamesAndAddressesHasPendingChanges() || (() => {
          const { snapshot, draft } = reconstructNamesAndAddresses();
          return Object.keys(validateNamesAndAddressesDraft(draft, snapshot)).length === 0;
        })();
    const classCodesAreValid = isTabMounted('classCodes')
      ? (classCodesRef.current?.validateForSave() ?? true)
      : !getClassCodesHasPendingChanges() || (() => {
          const { draftRows } = reconstructClassCodes();
          return Object.keys(
            validateClassCodeRows(
              draftRows,
              premiumDetails?.data?.[0]?.policyPeriodEffectiveDate,
              (classCodeLookupsData?.data ?? []).filter((option) => !option.statCodeIndicator),
              (classCodeLookupsData?.data ?? []).filter((option) => option.statCodeIndicator)
            )
          ).length === 0;
        })();
    const endorsementsAreValid = isTabMounted('endorsements')
      ? (endorsementsRef.current?.validateForSave() ?? true)
      : !getEndorsementsHasPendingChanges() || Object.keys(
          validateEndorsementRows(
            reconstructEndorsements().draftRows,
            endorsementTypeLookupsData?.data ?? [],
            viewPolicy.policyEffectiveDate
          )
        ).length === 0;
    const premiumIsValid = isTabMounted('premium')
      ? (premiumRef.current?.validateForSave() ?? true)
      : !getPremiumHasPendingChanges() || /^\d{1,10}$/.test(reconstructPremium().draftValue.trim());
    const unitStatReportsAreValid = isTabMounted('unitStatReports')
      ? (unitStatReportsRef.current?.validateForSave() ?? true)
      : !getUnitStatReportsHasPendingChanges() || (() => {
          const { report, snapshot, draft, exposureDraft, otherCorrections } = reconstructUnitStatReports();
          const primaryIsValid = Object.keys(validateUnitStatHeaderDraft(draft, report?.correctionSequenceNumber, snapshot)).length === 0;
          const primaryExposureIsValid = Object.keys(
            validateUnitStatExposureDraft(exposureDraft, classCodeLookupsData?.data ?? [])
          ).length === 0;
          return primaryIsValid && primaryExposureIsValid && otherCorrections.every((correction) =>
            Object.keys(
              validateUnitStatHeaderDraft(correction.draft, correction.report.correctionSequenceNumber, correction.snapshot)
            ).length === 0 &&
            Object.keys(validateUnitStatExposureDraft(correction.exposureDraft, classCodeLookupsData?.data ?? [])).length === 0
          );
        })();
    const isValidForSave = policyInformationIsValid && policyPeriodsAreValid && namesAndAddressesAreValid && classCodesAreValid && endorsementsAreValid && premiumIsValid && unitStatReportsAreValid;

    if (!isValidForSave) {
      setPolicyInfoTabHasErrors(!policyInformationIsValid);
      setNamesAndAddressesTabHasErrors(!namesAndAddressesAreValid);
      setClassCodesTabHasErrors(!classCodesAreValid);
      setEndorsementsTabHasErrors(!endorsementsAreValid);
      setPremiumTabHasErrors(!premiumIsValid);
      setUnitStatReportsTabHasErrors(!unitStatReportsAreValid);
      return;
    }

    setPolicyInfoTabHasErrors(false);
    setNamesAndAddressesTabHasErrors(false);
    setClassCodesTabHasErrors(false);
    setEndorsementsTabHasErrors(false);
    setPremiumTabHasErrors(false);
    setUnitStatReportsTabHasErrors(false);
    setSaveNote('');
    setSaveNoteError('');
    setSaveErrorMessage('');
    setChangeSummaryLines([
      ...(isTabMounted('policyInformation')
        ? (policyInformationRef.current?.getChangedFieldSummaries() ?? [])
        : getPolicyInformationHasPendingChanges()
          ? buildChangedFieldSummaryLines(reconstructPolicyInformation().snapshot, reconstructPolicyInformation().draft, editLookups)
          : []),
      ...(isTabMounted('policyPeriods')
        ? (policyPeriodsRef.current?.getChangedFieldSummaries() ?? [])
        : getPolicyPeriodsHasPendingChanges() ? (() => {
            const { snapshots, drafts } = reconstructPolicyPeriods();
            return [...drafts.entries()].flatMap(([periodId, draft]) => {
              const snapshot = snapshots.get(periodId)!;
              return buildPolicyPeriodChangedFieldSummaries(snapshot, draft, editLookups?.experienceRatingLookups ?? []).map(
                (summary) => `Period ${snapshot.effectiveDate}: ${summary}`
              );
            });
          })() : []),
      ...(isTabMounted('namesAndAddresses')
        ? (namesAndAddressesRef.current?.getChangedFieldSummaries() ?? [])
        : getNamesAndAddressesHasPendingChanges() ? getNamesAndAddressesChangedFieldSummaries(
            reconstructNamesAndAddresses().snapshot,
            reconstructNamesAndAddresses().draft,
            editLookups?.stateLookups ?? []
          ) : []),
      ...(isTabMounted('classCodes')
        ? (classCodesRef.current?.getChangedFieldSummaries() ?? [])
        : getClassCodesHasPendingChanges() ? (() => {
            const { snapshotRows, draftRows } = reconstructClassCodes();
            return getClassCodesChangedFieldSummaries(snapshotRows, draftRows, classCodeLookupsData?.data ?? []);
          })() : []),
      ...(isTabMounted('endorsements')
        ? (endorsementsRef.current?.getChangedFieldSummaries() ?? [])
        : getEndorsementsHasPendingChanges() ? (() => {
            const { snapshotRows, draftRows } = reconstructEndorsements();
            return getEndorsementsChangedFieldSummaries(
              snapshotRows,
              draftRows,
              endorsementTypeLookupsData?.data ?? [],
              endorsementDescriptorTypeLookupsData?.data ?? []
            );
          })() : []),
      ...(isTabMounted('premium')
        ? (premiumRef.current?.getChangedFieldSummaries() ?? [])
        : getPremiumHasPendingChanges() ? (() => {
            const { snapshotValue, draftValue } = reconstructPremium();
            return snapshotValue === draftValue
              ? []
              : [`Total policy premium changed from ${formatDollarAmount(Number(snapshotValue))} to ${formatDollarAmount(Number(draftValue))}`];
          })() : []),
      ...(isTabMounted('unitStatReports')
        ? (unitStatReportsRef.current?.getChangedFieldSummaries() ?? [])
        : getUnitStatReportsHasPendingChanges() ? (() => {
            const { report, snapshot, draft, lookups, exposureSnapshot, exposureDraft, otherCorrections } = reconstructUnitStatReports();
            const primarySummaries = report && Object.keys(getUnitStatHeaderDraftDelta(snapshot, draft)).length > 0
              ? getUnitStatHeaderChangedFieldSummaries(report, draft, lookups)
              : [];
            const primaryExposureSummaries = report
              ? getUnitStatExposureChangedFieldSummaries(
                  exposureSnapshot,
                  exposureDraft,
                  unitStatUsrLookupsData?.data?.splitPeriodTypes ?? []
                )
              : [];
            const otherSummaries = otherCorrections.flatMap((correction) =>
              [
                ...(Object.keys(getUnitStatHeaderDraftDelta(correction.snapshot, correction.draft)).length > 0
                  ? getUnitStatHeaderChangedFieldSummaries(correction.report, correction.draft, lookups).map(
                      (line) => `Correction ${correction.key} ${line}`
                    )
                  : []),
                ...getUnitStatExposureChangedFieldSummaries(
                  correction.exposureSnapshot,
                  correction.exposureDraft,
                  unitStatUsrLookupsData?.data?.splitPeriodTypes ?? []
                ).map((line) => `Correction ${correction.key} ${line}`),
              ]
            );
            return [...primarySummaries, ...primaryExposureSummaries, ...otherSummaries];
          })() : []),
    ]);
    setSaveConfirmOpen(true);
  };

  const handleConfirmSave = async () => {
    if (isSubmittingEdit) {
      return;
    }

    if (saveNote.trim().length === 0) {
      setSaveNoteError('Note is required.');
      return;
    }

    setSaveNoteError('');

    try {
      setIsSubmittingEdit(true);
      const request: UpdatePolicyRequest = {
        eventDescription: saveNote.trim(),
      };

      // Policy information payload/summary, mounted-ref path or reconstructed-draft path.
      const policyInformationSectionPayload = isTabMounted('policyInformation')
        ? policyInformationRef.current?.getPolicySectionPayload()
        : buildPolicySectionPayload(reconstructPolicyInformation().draft);
      const policyInformationStatePremiumsPayload = isTabMounted('policyInformation')
        ? (policyInformationRef.current?.getStatePremiumsPayload() ?? [])
        : buildStatePremiumsPayload(reconstructPolicyInformation().snapshot, reconstructPolicyInformation().draft);
      const policyChanges = getPolicyInformationHasPendingChanges();

      const policyPeriodsPayload = isTabMounted('policyPeriods')
        ? (policyPeriodsRef.current?.getPolicyPeriodsPayload() ?? [])
        : (() => {
            const { snapshots, drafts } = reconstructPolicyPeriods();
            return [...drafts.entries()]
              .map(([periodId, draft]) => buildPolicyPeriodPayload(snapshots.get(periodId)!, draft))
              .filter((payload): payload is NonNullable<typeof payload> => payload !== null);
          })();

      const namesAndAddressesChanges = getNamesAndAddressesHasPendingChanges();
      const namesAndAddressesPayload = isTabMounted('namesAndAddresses')
        ? namesAndAddressesRef.current?.getNamesAndAddressesPayload()
        : (() => {
            const { snapshot, draft } = reconstructNamesAndAddresses();
            return buildNamesAndAddressesPayload(snapshot, draft);
          })();

      const classCodeChanges = getClassCodesHasPendingChanges();
      const classCodesExposuresByPeriod = isTabMounted('classCodes')
        ? (classCodesRef.current?.getExposuresPayloadByPeriod() ?? [])
        : (() => {
            const { snapshotRows, draftRows } = reconstructClassCodes();
            return getClassCodesExposuresPayloadByPeriod(
              snapshotRows,
              draftRows,
              policyPeriodsRef.current?.getPrimaryPolicyPeriodId() ?? policyPeriodsData?.data?.[0]?.policyPeriodId
            );
          })();
      const classCodesDeletedByPeriod = isTabMounted('classCodes')
        ? (classCodesRef.current?.getDeletedPolicyExposureIdsByPeriod() ?? [])
        : getClassCodesDeletedPolicyExposureIdsByPeriod(reconstructClassCodes().deletedIds);

      const premiumChanges = getPremiumHasPendingChanges();
      const premiumPeriodPayload = isTabMounted('premium')
        ? premiumRef.current?.getPolicyPeriodPayload()
        : (() => {
            const { snapshotValue, draftValue } = reconstructPremium();
            const resolvedPremiumPeriodId =
              policyPeriodsRef.current?.getPrimaryPolicyPeriodId() ?? premiumDetails?.data?.[0]?.policyStatePremium?.policyPeriodId;
            return snapshotValue === draftValue || !resolvedPremiumPeriodId
              ? null
              : { policyPeriodId: resolvedPremiumPeriodId, estimatedStandardPremium: Number(draftValue) };
          })();

      const endorsementChanges = getEndorsementsHasPendingChanges();
      const endorsementsPayload = isTabMounted('endorsements')
        ? (endorsementsRef.current?.getEndorsementsPayload() ?? [])
        : toEndorsementsPayload(reconstructEndorsements().draftRows);

      const unitStatReportsPayload = isTabMounted('unitStatReports')
        ? (unitStatReportsRef.current?.getUnitStatReportsPayload() ?? [])
        : (() => {
            const { report, draft, exposureSnapshot, exposureDraft, otherCorrections } = reconstructUnitStatReports();
            const payloads = [];
            const headerChanged = report && Object.keys(getUnitStatHeaderDraftDelta(createUnitStatHeaderDraft(report), draft)).length > 0;
            const exposureChanged = report && JSON.stringify(exposureSnapshot) !== JSON.stringify(exposureDraft);
            if (report && (headerChanged || exposureChanged)) {
              const exposureRequest = createUnitStatHeaderUpdateRequest(report, draft);
              if (exposureChanged) {
                exposureRequest.exposures = createUnitStatExposureRequests(exposureDraft);
                exposureRequest.deletedExposureIds = exposureDraft.deletedExposureIds;
              }
              payloads.push(exposureRequest);
            }
            otherCorrections.forEach((correction) => {
              const correctionHeaderChanged = Object.keys(getUnitStatHeaderDraftDelta(correction.snapshot, correction.draft)).length > 0;
              const correctionExposureChanged = JSON.stringify(correction.exposureSnapshot) !== JSON.stringify(correction.exposureDraft);
              if (correctionHeaderChanged || correctionExposureChanged) {
                const exposureRequest = createUnitStatHeaderUpdateRequest(correction.report, correction.draft);
                if (correctionExposureChanged) {
                  exposureRequest.exposures = createUnitStatExposureRequests(correction.exposureDraft);
                  exposureRequest.deletedExposureIds = correction.exposureDraft.deletedExposureIds;
                }
                payloads.push(exposureRequest);
              }
            });
            return payloads;
          })();

      const periodsById = new Map((policyPeriodsPayload ?? []).map((item) => [item.policyPeriodId, { ...item }]));

      // PolicyInformation edits state premiums that can belong to any policy period; attribute each
      // changed row to its owning period using the policy's full state-premium list as the lookup.
      const statePremiumPeriodLookup = new Map(
        (viewPolicy.policyStatePremiums ?? []).map((item) => [item.statePremiumId, item.policyPeriodId])
      );
      for (const item of policyInformationStatePremiumsPayload ?? []) {
        const targetPeriodId = statePremiumPeriodLookup.get(item.statePremiumId);
        if (!targetPeriodId) {
          continue;
        }

        const existingPeriod = periodsById.get(targetPeriodId) ?? { policyPeriodId: targetPeriodId };
        const otherStatePremiums = (existingPeriod.statePremiums ?? []).filter(
          (statePremium) => statePremium.statePremiumId !== item.statePremiumId
        );
        periodsById.set(targetPeriodId, {
          ...existingPeriod,
          statePremiums: [...otherStatePremiums, item],
        });
      }

      if (policyChanges) {
        request.policy = policyInformationSectionPayload;
      }

      if (classCodeChanges) {
        for (const group of classCodesExposuresByPeriod) {
          const existingPeriod = periodsById.get(group.policyPeriodId) ?? { policyPeriodId: group.policyPeriodId };
          periodsById.set(group.policyPeriodId, { ...existingPeriod, exposures: group.exposures });
        }

        for (const group of classCodesDeletedByPeriod) {
          const existingPeriod = periodsById.get(group.policyPeriodId) ?? { policyPeriodId: group.policyPeriodId };
          periodsById.set(group.policyPeriodId, { ...existingPeriod, deletedPolicyExposureIds: group.deletedPolicyExposureIds });
        }
      }

      if (periodsById.size > 0) {
        request.policyPeriods = [...periodsById.values()];
      }

      if (namesAndAddressesChanges) {
        request.namesAndAddresses = namesAndAddressesPayload;
      }
      if (endorsementChanges) {
        request.endorsements = endorsementsPayload;
      }
      if (premiumChanges) {
        const premiumPeriod = premiumPeriodPayload;
        if (premiumPeriod) {
          const existingPeriod = periodsById.get(premiumPeriod.policyPeriodId) ?? {
            policyPeriodId: premiumPeriod.policyPeriodId,
          };
          periodsById.set(premiumPeriod.policyPeriodId, {
            ...existingPeriod,
            estimatedStandardPremium: premiumPeriod.estimatedStandardPremium,
          });
        }
      }
      if (periodsById.size > 0) {
        request.policyPeriods = [...periodsById.values()];
      }
      if (unitStatReportsPayload.length > 0) {
        request.unitStatReports = unitStatReportsPayload;
      }
      if (periodsById.size > 0) {
        request.policyPeriods = [...periodsById.values()];
      }
      setEditSession({ snapshot: editSession.snapshot, draft: request });

      try {
        await updatePolicy.mutateAsync({
          comboId: Number(comboId),
          coverageId: Number(coverageId),
          policyId,
          request,
        });
        await refetch();
        await Promise.all([
          queryClient.refetchQueries({ queryKey: ['policyPremiumsData', Number(comboId), Number(coverageId), policyId], type: 'all' }),
          queryClient.refetchQueries({ queryKey: ['policyPeriodsData', Number(comboId), Number(coverageId), policyId], type: 'all' }),
          queryClient.refetchQueries({ queryKey: ['policyExposuresData', Number(comboId), Number(coverageId), policyId], type: 'all' }),
          queryClient.refetchQueries({ queryKey: ['policyEndorsementsData', Number(comboId), Number(coverageId), policyId], type: 'all' }),
          // Awaited explicitly (rather than relying on the mutation's fire-and-forget onSuccess refetch)
          // so the Unit Stat Report tab never renders stale exposures right after a save.
          queryClient.refetchQueries({ queryKey: ['policyUnitStatReport', Number(comboId), Number(coverageId), policyId], type: 'all' }),
        ]);
        policyInformationRef.current?.commitEdit();
        policyPeriodsRef.current?.commitEdit();
        namesAndAddressesRef.current?.commitEdit();
        classCodesRef.current?.commitEdit();
        endorsementsRef.current?.commitEdit();
        premiumRef.current?.commitEdit();
        unitStatReportsRef.current?.commitEdit();
        policyInformationDeltaRef.current = null;
        policyPeriodsDeltaRef.current = null;
        namesAndAddressesDeltaRef.current = null;
        classCodesDeltaRef.current = null;
        endorsementsDeltaRef.current = null;
        premiumDeltaRef.current = null;
        unitStatReportsDeltaRef.current = null;
        setEditSession({ snapshot: request, draft: request });
        setSaveConfirmOpen(false);
        setSaveNote('');
        setChangeSummaryLines([]);
        setSuccessMessage('Changes saved successfully');
        startTransition(() => {
          setIsEditMode(false);
          setPolicyInfoTabHasErrors(false);
          setNamesAndAddressesTabHasErrors(false);
          setClassCodesTabHasErrors(false);
          setEndorsementsTabHasErrors(false);
          setPremiumTabHasErrors(false);
          setUnitStatReportsTabHasErrors(false);
        });
      } catch (saveError) {
        const statusCode =
          typeof saveError === 'object' && saveError !== null && 'response' in saveError
            ? (saveError as { response?: { status?: number } }).response?.status
            : undefined;
        setSaveErrorMessage(
          statusCode === 400
            ? 'One or more fields require correction.'
            : 'A system error occurred. Please try again.'
        );
      }
    } finally {
      setIsSubmittingEdit(false);
    }
  };

  const handleCancelConfirmNoGoBack = () => {
    setCancelConfirmOpen(false);
  };

  const handleCancelConfirmYesCancel = () => {
    policyInformationRef.current?.cancelEdit();
    policyPeriodsRef.current?.cancelEdit();
    namesAndAddressesRef.current?.cancelEdit();
    classCodesRef.current?.cancelEdit();
    endorsementsRef.current?.cancelEdit();
    premiumRef.current?.cancelEdit();
    unitStatReportsRef.current?.cancelEdit();
    policyInformationDeltaRef.current = null;
    policyPeriodsDeltaRef.current = null;
    namesAndAddressesDeltaRef.current = null;
    classCodesDeltaRef.current = null;
    endorsementsDeltaRef.current = null;
    premiumDeltaRef.current = null;
    unitStatReportsDeltaRef.current = null;
    setEditSession({ snapshot: null, draft: null });
    setCancelConfirmOpen(false);
    startTransition(() => {
      setIsEditMode(false);
      setPolicyInfoTabHasErrors(false);
      setNamesAndAddressesTabHasErrors(false);
      setClassCodesTabHasErrors(false);
      setEndorsementsTabHasErrors(false);
      setPremiumTabHasErrors(false);
      setUnitStatReportsTabHasErrors(false);
    });
  };

  const handleSaveConfirmNoGoBack = () => {
    setSaveConfirmOpen(false);
    setSaveNoteError('');
    setSaveErrorMessage('');
  };

  const getTabLabel = (value: string, label: string): string => {
    if (value === 'policyInformation' && policyInfoTabHasErrors) {
      return `${label} *`;
    }

    if (value === 'namesAndAddresses' && namesAndAddressesTabHasErrors) {
      return `${label} *`;
    }

    if (value === 'classCodes' && classCodesTabHasErrors) {
      return `${label} *`;
    }

    if (value === 'endorsements' && endorsementsTabHasErrors) {
      return `${label} *`;
    }

    if (value === 'premium' && premiumTabHasErrors) {
      return `${label} *`;
    }

    if (value === 'unitStatReports' && unitStatReportsTabHasErrors) {
      return `${label} *`;
    }

    return label;
  };

  const renderTabComponent = (tabValue: string) => {
    const primaryPolicyPeriodId =
      policyPeriodsRef.current?.getPrimaryPolicyPeriodId() || policyPeriodsData?.data?.[0]?.policyPeriodId;
    switch (tabValue) {
      case 'policyInformation':
        return (
          <PolicyInformation
            ref={policyInformationRef}
            comboId={Number(comboId)}
            coverageId={Number(coverageId)}
            policyId={policyId}
            viewPolicy={viewPolicy}
            isEditMode={isEditMode}
            onRefresh={handleRefreshPolicy}
            initialDraftDelta={policyInformationDeltaRef.current}
            onDraftDeltaChange={(delta) => {
              policyInformationDeltaRef.current = Object.keys(delta).length > 0 ? delta : null;
            }}
          />
        );
      case 'policyPeriods':
        return (
          <PolicyPeriods
            ref={policyPeriodsRef}
            comboId={Number(comboId)}
            coverageId={Number(coverageId)}
            policyId={policyId}
            isEditMode={isEditMode}
            experienceRatingLookups={editLookups?.experienceRatingLookups}
            initialDraftDelta={policyPeriodsDeltaRef.current}
            onDraftDeltaChange={(delta) => {
              policyPeriodsDeltaRef.current = Object.keys(delta).length > 0 ? delta : null;
            }}
          />
        );
      case 'namesAndAddresses':
        return (
          <NamesAndAddresses
            ref={namesAndAddressesRef}
            comboId={Number(comboId)}
            coverageId={Number(coverageId)}
            policyId={policyId}
            isEditMode={isEditMode}
            stateLookups={editLookups?.stateLookups ?? EMPTY_STATE_LOOKUPS}
            initialDraftDelta={namesAndAddressesDeltaRef.current}
            onDraftDeltaChange={(delta) => {
              namesAndAddressesDeltaRef.current = Object.keys(delta).length > 0 ? delta : null;
            }}
          />
        );
      case 'premium':
        return (
          <Premium
            ref={premiumRef}
            isEditMode={isEditMode}
            policyPeriodId={primaryPolicyPeriodId}
            initialDraftDelta={premiumDeltaRef.current}
            onDraftDeltaChange={(delta) => {
              premiumDeltaRef.current = Object.keys(delta).length > 0 ? delta : null;
            }}
          />
        );
      case 'classCodes':
        return (
          <ClassCodes
            ref={classCodesRef}
            isEditMode={isEditMode}
            policyPeriodEffectiveDate={premiumDetails?.data?.[0]?.policyPeriodEffectiveDate}
            policyPeriodId={primaryPolicyPeriodId}
            initialDraftDelta={classCodesDeltaRef.current}
            onDraftDeltaChange={(delta) => {
              classCodesDeltaRef.current = Object.keys(delta).length > 0 ? delta : null;
            }}
          />
        );
      case 'unitStatReports':
        return (
          <UnitStatReports
            ref={unitStatReportsRef}
            isEditMode={isEditMode}
            initialDraftDelta={unitStatReportsDeltaRef.current}
            onDraftDeltaChange={(delta) => {
              unitStatReportsDeltaRef.current = delta && Object.keys(delta).length > 0 ? delta : null;
            }}
          />
        );
      case 'endorsements':
        return (
          <Endorsements
            ref={endorsementsRef}
            isEditMode={isEditMode}
            policyEffectiveDate={viewPolicy.policyEffectiveDate}
            initialDraftDelta={endorsementsDeltaRef.current}
            onDraftDeltaChange={(delta) => {
              endorsementsDeltaRef.current = Object.keys(delta).length > 0 ? delta : null;
            }}
          />
        );
      case 'statusHistory':
        return <StatusHistory />;
      case 'complianceHistory':
        return <ComplianceHistory />;
      case 'transactionHistory':
        return <TransactionHistory />;
      default:
        return null;
    }
  };

  const renderContent = () => (
    <TabContext value={tab}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Typography variant="h4">
            {primaryPolicyName !== undefined
              ? `${primaryPolicyName.insuredName} - ${viewPolicy?.policyNumber}`
              : 'Policy Page'}
            <PolicyStatusChips
              isActive={viewPolicy?.isActive}
              isCancelled={viewPolicy?.isCancelled}
              isNonCompliant={viewPolicy?.isNonCompliant}
            />
          </Typography>
          <MovePolicyButton onClick={movePolicyModal.onTrue} />
        </Box>
        <DynamicBreadcrumbs crumbStartPath="combo" />
        <PageAlertMessage
          autoHideDuration={4000}
          message={successMessage}
          onClose={() => setSuccessMessage('')}
          open={Boolean(successMessage)}
          position="bottom-right"
          severity="success"
          testId="policy-details-save-success"
        />
        <PageAlertMessage
          message={saveErrorMessage}
          onClose={() => setSaveErrorMessage('')}
          open={Boolean(saveErrorMessage)}
          position="top-center"
          severity="error"
          testId="policy-details-save-error"
        />
        {canNavigateBackToSearch ? (
          <Box sx={{ mt: 1, mb: 1 }}>
            <ActionButton
              label="Back to search"
              testId="policy-details-back-to-search"
              onClick={handleBackToSearchClick}
              icon="lucide:arrow-left"
              iconPosition="start"
              variant="text"
            />
          </Box>
        ) : null}
        {tab === 'policyInformation' || tab === 'policyPeriods' || tab === 'namesAndAddresses' || tab === 'premium' || tab === 'classCodes' || tab === 'endorsements' || tab === 'unitStatReports' ? (
          <Box sx={{ mt: 1, mb: 1, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            {!isEditMode ? (
              <ActionButton
                label="Edit Policy"
                testId="policy-details-edit-policy"
                onClick={handleEnterEdit}
                icon="lucide:pencil"
                iconPosition="start"
                styleVariant="light-outlined"
              />
            ) : (
              <>
                <ActionButton
                  label="Cancel"
                  testId="policy-details-cancel-edit"
                  onClick={handleCancelEdit}
                  disabled={isSubmittingEdit}
                  styleVariant="light-outlined"
                  icon="lucide:x"
                  iconPosition="start"
                />
                <ActionButton
                  label="Save"
                  testId="policy-details-submit-edit"
                  onClick={handleOpenSaveConfirm}
                  disabled={isSubmittingEdit}
                  icon="lucide:check"
                  iconPosition="end"
                />
              </>
            )}
          </Box>
        ) : null}
        <TabList
          onChange={handleTabChange}
          data-test-id="employer-data-policy-tabs"
          aria-label="employer data policy tabs"
        >
          {ED_POLICY_TABS.map((currentTab) => (
            <Tab
              key={currentTab.value}
              value={currentTab.value}
              label={getTabLabel(currentTab.value, currentTab.label)}
              data-testid={`${currentTab.value}`}
            />
          ))}
        </TabList>
      </Box>
      <TabPanel value={tab} sx={{ px: 0 }}>
        {isEditMode && isTabPending ? <TabContentSkeleton /> : renderTabComponent(tab)}
      </TabPanel>
      <MovePolicyModal
        open={movePolicyModal.value}
        onClose={movePolicyModal.onFalse}
        comboId={Number(comboId)}
        coverageId={Number(coverageId)}
        policyId={policyId}
      />
      <SaveConfirmModal
        open={saveConfirmOpen}
        note={saveNote}
        noteError={saveNoteError}
        changedFieldSummaries={changeSummaryLines}
        isSaving={isSubmittingEdit}
        onClose={handleSaveConfirmNoGoBack}
        onNoteChange={(value) => {
          setSaveNote(value);
          if (saveNoteError) {
            setSaveNoteError('');
          }
        }}
        onConfirmSave={handleConfirmSave}
      />
      <CancelConfirmModal
        open={cancelConfirmOpen}
        onClose={handleCancelConfirmNoGoBack}
        onConfirmCancel={handleCancelConfirmYesCancel}
      />
      <CancelConfirmModal
        open={leavePageBlocker.state === 'blocked'}
        content="If you leave this page, your changes will not be saved. Do you want to continue?"
        onClose={() => leavePageBlocker.reset?.()}
        onConfirmCancel={() => leavePageBlocker.proceed?.()}
      />
    </TabContext>
  );

  return <>{renderContent()}</>;
}
